import {
  MarketAccessory,
  MarketAccordionItem,
  MarketButton,
  MarketPill,
  MarketRow,
} from '@market/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketNoEntryCircleIcon } from '@squareup/market-react/icons';
import { withI18n } from '@squareup/service-i18n/components';
import { InstantAccessProgram_Platform } from '@squareup/shared-types-protos/squareup/inflows/instant_access_program_model';
import {
  type InstantAccessAccordionItem,
  type InstantAccessRequirementItem,
  InstantAccessAccordionItem_Status,
  InstantAccessRequirementItem_Type,
} from '@squareup/shared-types-protos/squareup/inflows/service/external_instant_access_service';
import BankSvg from '../../assets/svgs/bank';
import DualRotatingArrowSvg from '../../assets/svgs/dual-rotating-arrow';
import SignDollarArrowSvg from '../../assets/svgs/sign-dollar-arrow';
import useResponsiveBreakpoint from '../../hooks/useResponsiveBreakpoint';
import { getPlatformBranding } from '../../utils/platformBranding';
import styles from './ChecklistAccordion.module.scss';

type MarketPillVariant = 'emphasis' | 'normal' | 'success' | 'warning' | 'critical' | 'insight' | 'alpha' | 'beta' | undefined;

export interface LoadingRequirementKey {
  platform: InstantAccessProgram_Platform;
  type: InstantAccessRequirementItem_Type;
}

export interface ChecklistAccordionProps {
  instantAccessAccordionItems: InstantAccessAccordionItem[];
  loadingRequirementKey?: LoadingRequirementKey;
  onRequirementItemClick: (
    requirementItem: InstantAccessRequirementItem,
    accordionItem: InstantAccessAccordionItem
  ) => void;
}

const isRequirementLoading = (
  reqItem: InstantAccessRequirementItem,
  accordionItem: InstantAccessAccordionItem,
  loadingKey?: LoadingRequirementKey,
): boolean => {
  if (!loadingKey) {
    return false;
  }
  return (
    reqItem.type === loadingKey.type &&
    accordionItem.platform === loadingKey.platform
  );
};

export const ChecklistAccordion = ({
  instantAccessAccordionItems,
  onRequirementItemClick,
  loadingRequirementKey,
}: ChecklistAccordionProps) => {
  const { t } = useTranslation();
  const isMobile = useResponsiveBreakpoint();

  const [expandedItems, setExpandedItems] = useState<Set<InstantAccessProgram_Platform>>(new Set());

  const toggleAccordion = (platform: InstantAccessProgram_Platform) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) {
        next.delete(platform);
      } else {
        next.add(platform);
      }
      return next;
    });
  };

  const getPlatformLabel = (platform?: InstantAccessProgram_Platform): string => {
    switch (platform) {
      case InstantAccessProgram_Platform.DOORDASH:
        return t('bankingInstantPayouts.platforms.doordash');
      case InstantAccessProgram_Platform.UBER_EATS:
        return t('bankingInstantPayouts.platforms.uberEats');
      case InstantAccessProgram_Platform.GRUBHUB:
        return t('bankingInstantPayouts.platforms.grubhub');
      default:
        return '';
    }
  };

  const getStatusPillVariant = (status?: InstantAccessAccordionItem_Status): MarketPillVariant => {
    switch (status) {
      case InstantAccessAccordionItem_Status.GET_STARTED:
        return 'emphasis';
      case InstantAccessAccordionItem_Status.IN_PROGRESS:
      case InstantAccessAccordionItem_Status.CANCELING:
        return 'warning';
      case InstantAccessAccordionItem_Status.ACTIVE:
        return 'success';
      case InstantAccessAccordionItem_Status.COMING_SOON:
        return 'normal';
      default:
        return undefined;
    }
  };

  const getPillLabel = (status?: InstantAccessAccordionItem_Status): string | undefined => {
    switch (status) {
      case InstantAccessAccordionItem_Status.GET_STARTED:
        return t('bankingInstantPayouts.checklistAccordion.pillLabels.getStarted');
      case InstantAccessAccordionItem_Status.COMING_SOON:
        return t('bankingInstantPayouts.checklistAccordion.pillLabels.comingSoon');
      case InstantAccessAccordionItem_Status.IN_PROGRESS:
        return t('bankingInstantPayouts.checklistAccordion.pillLabels.finishSetUp');
      case InstantAccessAccordionItem_Status.ACTIVE:
        return t('bankingInstantPayouts.checklistAccordion.pillLabels.active');
      case InstantAccessAccordionItem_Status.CANCELING:
        return t('bankingInstantPayouts.checklistAccordion.pillLabels.canceling');
      default:
        return undefined;
    }
  };

  const getRequirementItemLabelAndCTALabel = (requirementItemType?: InstantAccessRequirementItem_Type, platform?: InstantAccessProgram_Platform) => {
    const platformLabel = getPlatformLabel(platform);
    switch (requirementItemType) {
      case InstantAccessRequirementItem_Type.ORDERS_INTEGRATION_SETUP:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.integrationSetup.label', { platformLabel }),
          ctaLabel: t('bankingInstantPayouts.checklistAccordion.requirementItems.integrationSetup.ctaLabel'),
        };
      case InstantAccessRequirementItem_Type.ACTIVE_CHECKING_ACCOUNT:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.openSquareCheckingAccount.label'),
          ctaLabel: isMobile ?
            t('bankingInstantPayouts.checklistAccordion.requirementItems.openSquareCheckingAccount.ctaLabel.mobile')
            : t('bankingInstantPayouts.checklistAccordion.requirementItems.openSquareCheckingAccount.ctaLabel.desktop'),
        };
      case InstantAccessRequirementItem_Type.DIRECT_DEPOSITS_SETUP:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.directDepositSetup.label'),
          ctaLabel: isMobile ?
            t('bankingInstantPayouts.checklistAccordion.requirementItems.directDepositSetup.ctaLabel.mobile')
            : t('bankingInstantPayouts.checklistAccordion.requirementItems.directDepositSetup.ctaLabel.desktop'),
        };
      case InstantAccessRequirementItem_Type.DEACTIVATE_INSTANT_PAYOUTS:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.deactivateInstantPayouts.label'),
          ctaLabel: t('bankingInstantPayouts.checklistAccordion.requirementItems.deactivateInstantPayouts.ctaLabel'),
        };
      case InstantAccessRequirementItem_Type.REACTIVATE_INSTANT_PAYOUTS:
        return {
          label: t('bankingInstantPayouts.checklistAccordion.requirementItems.reactivateInstantPayouts.label'),
          ctaLabel: t('bankingInstantPayouts.checklistAccordion.requirementItems.reactivateInstantPayouts.ctaLabel'),
        };
      default:
        return {};
    }
  };

  // render functions
  const renderStaticAccordionItem = (item: InstantAccessAccordionItem) => {
    const platformLabel = getPlatformLabel(item.platform);
    const branding = getPlatformBranding(item.platform);
    return (
      <div key={platformLabel} className={styles.checklistAccordionWrapper}>
        <MarketRow
          data-testid="static-checklist-accordion"
          className={styles.staticChecklistAccordionItem}
          size="small"
        >
          <MarketAccessory size="image" slot="leading-accessory">
            <img
              className={styles.checklistAccordionLabelLogo}
              src={branding?.logoImageUrl}
              alt=""
              aria-hidden="true"
              style={{ backgroundColor: branding?.logoImageBackgroundColorCode }}
            />
          </MarketAccessory>
          <label slot="label"><h3>{platformLabel}</h3></label>
          <MarketPill
            className={styles.checklistAccordionLabelPill}
            size="medium"
            slot="trailing-accessory"
            variant={getStatusPillVariant(item.status)}
          >
            {getPillLabel(item.status)}
          </MarketPill>
        </MarketRow>
      </div>
    );
  };

  const renderRequirementRowSvg = (type?: InstantAccessRequirementItem_Type) => {
    switch (type) {
      case InstantAccessRequirementItem_Type.ACTIVE_CHECKING_ACCOUNT:
        return <BankSvg />;
      case InstantAccessRequirementItem_Type.DIRECT_DEPOSITS_SETUP:
        return <SignDollarArrowSvg />;
      case InstantAccessRequirementItem_Type.ORDERS_INTEGRATION_SETUP:
        return <DualRotatingArrowSvg />;
      case InstantAccessRequirementItem_Type.REACTIVATE_INSTANT_PAYOUTS:
        return <DualRotatingArrowSvg />;
      case InstantAccessRequirementItem_Type.DEACTIVATE_INSTANT_PAYOUTS:
        return <MarketNoEntryCircleIcon aria-hidden />;
      default:
        return null;
    }
  };

  const renderRequirementRows = (item: InstantAccessAccordionItem) => (
    item.requirementItems?.map((reqItem) => {
      const { label, ctaLabel } = getRequirementItemLabelAndCTALabel(reqItem.type, item.platform);

      return (<MarketRow
        key={label}
        transient
        data-testid="checklist-accordion-requirement-row"
        className={styles.checklistAccordionRow}
      >
        <MarketAccessory size="icon" slot="leading-accessory">
          {renderRequirementRowSvg(reqItem.type)}
        </MarketAccessory>

        <label slot="label">
          {label}
        </label>

        <MarketButton
          data-testid="checklist-accordion-requirement-row-cta"
          slot="trailing-accessory"
          disabled={reqItem.isDisabled}
          isLoading={isRequirementLoading(reqItem, item, loadingRequirementKey)}
          onClick={() => onRequirementItemClick(reqItem, item)}
        >
          {ctaLabel}
        </MarketButton>
      </MarketRow>);
    })
  );

  const handleAccordionToggle = (item: InstantAccessAccordionItem) => (e: CustomEvent) => {
    e.preventDefault();
    if (item.platform) {
      toggleAccordion(item.platform);
    }
  };

  const renderDynamicAccordionItem = (item: InstantAccessAccordionItem) => {
    const platformLabel = getPlatformLabel(item.platform);
    const branding = getPlatformBranding(item.platform);
    return (
      <div key={platformLabel} className={styles.checklistAccordionWrapper}>
        <MarketAccordionItem
          className={styles.checklistAccordionItem}
          data-testid="checklist-accordion"
          id={platformLabel}
          name={platformLabel}
          disabled={item.isDisabled}
          expanded={item.platform !== undefined && expandedItems.has(item.platform)}
          onMarketAccordionItemExpandedChange={handleAccordionToggle(item)}
        >
          <span slot="label" className={styles.checklistAccordionLabel}>
            <span className={styles.checklistAccordionLabelTitle}>
              <img
                className={styles.checklistAccordionLabelLogo}
                src={branding?.logoImageUrl}
                alt=""
                aria-hidden="true"
                style={{ backgroundColor: branding?.logoImageBackgroundColorCode }}
              />
              <label>{platformLabel}</label>
            </span>
            <MarketPill
              className={styles.checklistAccordionLabelPill}
              size="medium"
              variant={getStatusPillVariant(item.status)}
            >
              {getPillLabel(item.status)}
            </MarketPill>
          </span>

          <div>
            {renderRequirementRows(item)}
          </div>
        </MarketAccordionItem>
      </div>
    );
  };

  return (
    <div className={styles.checklistAccordionContainer}>
      {instantAccessAccordionItems.map((item) => {
        // When accordion is already completed and has no requirement items, we show a static row as a mimic accordion
        // otherwise, show a real dynamic accordion
        const isAccordionCompleted = item.status === InstantAccessAccordionItem_Status.ACTIVE
          && (item.requirementItems?.length ?? 0) === 0;

        return isAccordionCompleted
          ? renderStaticAccordionItem(item)
          : renderDynamicAccordionItem(item);
      })}
    </div>
  );
};

export const ChecklistAccordionI18n = withI18n(ChecklistAccordion);
